// Browser side of auto-slice. The detector and trained model (shared/autoSlice) run on the
// server (server/api/v1/slicer/auto.post.ts) and are not imported here, so they stay out of
// the client bundle. This file only gets pixels off an <img>/<canvas> and posts them.

export type CutSuggestion = { y: number; score: number; features: number[]; confidence: number }
/** A vertical candidate: x within the rows y0..y1. */
export type VCutSuggestion = { x: number; y0: number; y1: number; confidence: number }

export interface AutoSliceResult {
  hCuts: number[]
  /** Per-band x positions, indexed like the slicer's vCuts. */
  vCuts: number[][]
}

type Source = HTMLImageElement | HTMLCanvasElement

function readPixels(source: Source, width: number, height: number): Uint8ClampedArray | null {
  if (!width || !height) return null
  const off = document.createElement('canvas')
  off.width = width
  off.height = height
  const ctx = off.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(source, 0, 0, width, height)
  return ctx.getImageData(0, 0, width, height).data
}

// The server holds one RGBA buffer in a 128MB Worker (MAX_PIXELS in server/utils/inflatePixels.ts).
const MAX_PIXELS = 20_000_000

// ponytail: designs over the cap are halved (or more) before detection and the cuts scaled
// back, so a cut can land a pixel or two off a band edge at full resolution. Snap cuts to the
// nearest edge client-side if that shows up in exported slices.
async function post<T>(mode: 'slice' | 'suggest', source: Source, w: number, h: number, extra: { hCuts?: number[]; min?: number } = {}): Promise<{ data: T; sx: number; sy: number } | null> {
  const k = Math.max(1, Math.ceil(Math.sqrt((w * h) / MAX_PIXELS)))
  const sw = Math.floor(w / k), sh = Math.floor(h / k)
  const pixels = readPixels(source, sw, sh)
  if (!pixels) return null
  // raw RGBA is ~29MB for a 1200×6000 design; flat design pixels gzip to a small fraction
  const body = await new Response(new Blob([pixels as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'))).blob()
  const data = await $fetch<T>('/api/v1/slicer/auto', {
    method: 'POST',
    query: { w: sw, h: sh, mode, min: extra.min, cuts: extra.hCuts?.map((y) => Math.round((y * sh) / h)).join(',') || undefined },
    headers: { 'content-type': 'application/octet-stream' },
    body,
  })
  return { data, sx: w / sw, sy: h / sh }
}

export function useAutoSlice() {
  /**
   * Candidates for the accept/dismiss suggestion flow, in the image's natural coordinates, with
   * ranker confidence (0.5 when no ranker is trained). Horizontal ones are every candidate
   * (high recall). Vertical ones sit in the bands formed by `hCuts` plus the cuts the model keeps.
   */
  async function suggestCuts(source: Source, naturalW: number, naturalH: number, hCuts: number[] = []) {
    const r = await post<{ h: CutSuggestion[]; v: VCutSuggestion[] }>('suggest', source, naturalW, naturalH, { hCuts })
    if (!r) return { h: [], v: [] }
    return {
      h: r.data.h.map((c) => ({ ...c, y: Math.round(c.y * r.sy) })),
      v: r.data.v.map((c) => ({ ...c, x: Math.round(c.x * r.sx), y0: Math.round(c.y0 * r.sy), y1: Math.round(c.y1 * r.sy) })),
    }
  }

  /**
   * Full one-click auto-slice: the horizontal and per-band vertical cuts the model keeps.
   * `min` (0 to 1) replaces the trained confidence cutoff: lower places more cuts.
   */
  async function autoSliceImage(source: Source, naturalW: number, naturalH: number, min?: number): Promise<AutoSliceResult | null> {
    const r = await post<AutoSliceResult>('slice', source, naturalW, naturalH, { min })
    if (!r) return null
    return {
      hCuts: r.data.hCuts.map((y) => Math.round(y * r.sy)),
      vCuts: r.data.vCuts.map((xs) => xs.map((x) => Math.round(x * r.sx))),
    }
  }

  return { suggestCuts, autoSliceImage }
}
